import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Download, FileSpreadsheet, Mail, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AVAILABLE_COLUMNS = [
  { key: 'employee_name', labelKey: 'expenses.columns.employee_name' },
  { key: 'expense_date', labelKey: 'expenses.columns.expense_date' },
  { key: 'project_name', labelKey: 'expenses.columns.project_name' },
  { key: 'category', labelKey: 'expenses.columns.category' },
  { key: 'subcategory', labelKey: 'expenses.columns.subcategory' },
  { key: 'description', labelKey: 'expenses.columns.description' },
  { key: 'vendor', labelKey: 'expenses.columns.vendor' },
  { key: 'invoice_number', labelKey: 'expenses.columns.invoice_number' },
  { key: 'amount', labelKey: 'expenses.columns.amount' },
  { key: 'currency', labelKey: 'expenses.columns.currency' },
  { key: 'amount_pln', labelKey: 'expenses.columns.amount_pln' },
  { key: 'exchange_rate', labelKey: 'expenses.columns.exchange_rate' },
  { key: 'payment_method', labelKey: 'expenses.columns.payment_method' },
  { key: 'status', labelKey: 'expenses.columns.status' },
  { key: 'has_receipt', labelKey: 'expenses.columns.has_receipt' }
];

const STATUS_OPTIONS = [
  { key: 'draft', labelKey: 'expenses.status.draft' },
  { key: 'submitted', labelKey: 'expenses.status.submitted' },
  { key: 'manager_approved', labelKey: 'expenses.status.manager_approved' },
  { key: 'admin_approved', labelKey: 'expenses.status.admin_approved' },
  { key: 'rejected', labelKey: 'expenses.status.rejected' },
  { key: 'paid', labelKey: 'expenses.status.paid' }
];

const MONTH_OPTIONS = [
  { value: 1, labelKey: 'months.m1' },
  { value: 2, labelKey: 'months.m2' },
  { value: 3, labelKey: 'months.m3' },
  { value: 4, labelKey: 'months.m4' },
  { value: 5, labelKey: 'months.m5' },
  { value: 6, labelKey: 'months.m6' },
  { value: 7, labelKey: 'months.m7' },
  { value: 8, labelKey: 'months.m8' },
  { value: 9, labelKey: 'months.m9' },
  { value: 10, labelKey: 'months.m10' },
  { value: 11, labelKey: 'months.m11' },
  { value: 12, labelKey: 'months.m12' }
];

const ExportExpensesDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  
  // Multi-select for months (stored as array of {year, month} objects)
  const [selectedMonths, setSelectedMonths] = useState([
    { year: currentYear, month: currentDate.getMonth() + 1 }
  ]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  
  // Multi-select for employees
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [expenseCreators, setExpenseCreators] = useState([]);

  // Multi-select for projects
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [projects, setProjects] = useState([]);

  // Multi-select for payment methods
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  
  const [selectedColumns, setSelectedColumns] = useState(AVAILABLE_COLUMNS.map(c => c.key));
  const [selectedStatuses, setSelectedStatuses] = useState(STATUS_OPTIONS.map(s => s.key));
  const [isExporting, setIsExporting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [includeReceipts, setIncludeReceipts] = useState(true);

  // Generate year options
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 3; y--) {
    yearOptions.push(y);
  }

  useEffect(() => {
    if (open) {
      fetchExpenseCreators();
      fetchProjects();
      fetchPaymentMethods();
    }
  }, [open]);

  const fetchExpenseCreators = async () => {
    try {
      const response = await axios.get(`${API}/expenses/creators`);
      setExpenseCreators(response.data);
    } catch (error) {
      console.error('Error fetching expense creators:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      // Reuse the company settings — payment_methods is persisted there.
      const r = await axios.get(`${API}/settings`);
      const list = Array.isArray(r.data?.payment_methods) ? r.data.payment_methods : [];
      setPaymentMethodOptions(list);
    } catch (e) {
      console.error('Error fetching payment methods:', e);
    }
  };

  const toggleProject = (projectId) => {
    setSelectedProjectIds(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };
  const selectAllProjects = () => setSelectedProjectIds(projects.map(p => p.id));
  const deselectAllProjects = () => setSelectedProjectIds([]);

  const togglePaymentMethod = (pm) => {
    setSelectedPaymentMethods(prev =>
      prev.includes(pm) ? prev.filter(p => p !== pm) : [...prev, pm]
    );
  };
  const selectAllPaymentMethods = () =>
    setSelectedPaymentMethods([...paymentMethodOptions, '__none__']);
  const deselectAllPaymentMethods = () => setSelectedPaymentMethods([]);

  const toggleMonth = (year, month) => {
    const key = `${year}-${month}`;
    const exists = selectedMonths.some(m => m.year === year && m.month === month);
    
    if (exists) {
      setSelectedMonths(prev => prev.filter(m => !(m.year === year && m.month === month)));
    } else {
      setSelectedMonths(prev => [...prev, { year, month }]);
    }
  };

  const isMonthSelected = (year, month) => {
    return selectedMonths.some(m => m.year === year && m.month === month);
  };

  const selectAllMonthsForYear = (year) => {
    const newMonths = MONTH_OPTIONS.map(m => ({ year, month: m.value }));
    setSelectedMonths(prev => {
      const otherYears = prev.filter(m => m.year !== year);
      return [...otherYears, ...newMonths];
    });
  };

  const deselectAllMonthsForYear = (year) => {
    setSelectedMonths(prev => prev.filter(m => m.year !== year));
  };

  const toggleEmployee = (employeeId) => {
    setSelectedEmployeeIds(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAllEmployees = () => {
    setSelectedEmployeeIds(expenseCreators.map(c => c.id));
  };

  const deselectAllEmployees = () => {
    setSelectedEmployeeIds([]);
  };

  const toggleColumn = (columnKey) => {
    setSelectedColumns(prev => 
      prev.includes(columnKey) 
        ? prev.filter(c => c !== columnKey)
        : [...prev, columnKey]
    );
  };

  const toggleStatus = (statusKey) => {
    setSelectedStatuses(prev => 
      prev.includes(statusKey) 
        ? prev.filter(s => s !== statusKey)
        : [...prev, statusKey]
    );
  };

  const handleExport = async () => {
    if (selectedMonths.length === 0) {
      toast.error(t('expenses.toasts.selectMonth'));
      return;
    }
    
    if (selectedColumns.length === 0) {
      toast.error(t('expenses.toasts.selectColumn'));
      return;
    }
    
    if (selectedStatuses.length === 0) {
      toast.error(t('expenses.toasts.selectStatus'));
      return;
    }

    setIsExporting(true);
    
    try {
      // If all employees are selected, send empty array to get ALL expenses
      // (including any that might not be in the creators list due to data inconsistencies)
      const employeeIdsToSend = selectedEmployeeIds.length === expenseCreators.length 
        ? [] // Empty array means "all employees" on the backend
        : selectedEmployeeIds;
      const projectIdsToSend = selectedProjectIds.length === projects.length
        ? [] // All projects selected -> no filter
        : selectedProjectIds;
      // Same convention for payment methods: empty == "all"
      const paymentMethodsToSend = (
        paymentMethodOptions.length > 0 &&
        selectedPaymentMethods.length === paymentMethodOptions.length + 1
      ) ? [] : selectedPaymentMethods;
      
      const response = await axios.post(
        `${API}/expenses/export`,
        {
          months: selectedMonths,
          employee_ids: employeeIdsToSend,
          project_ids: projectIdsToSend,
          payment_methods: paymentMethodsToSend,
          status_filter: selectedStatuses,
          columns: selectedColumns,
          include_receipts: includeReceipts
        },
        { responseType: 'blob' }
      );

      // Extract filename from header
      const contentDisposition = response.headers['content-disposition'];
      let filename = includeReceipts ? 'Expenses_Export_with_receipts.zip' : 'Expenses_Export.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', weekFileName(filename));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(t('expenses.toasts.exported'));
      onOpenChange(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('expenses.toasts.exportFailed') + (error.response?.data?.detail || error.message));
    } finally {
      setIsExporting(false);
    }
  };

  const handleSendEmail = async () => {
    if (selectedMonths.length === 0) {
      toast.error(t('expenses.toasts.selectMonth'));
      return;
    }
    if (!emailAddress) {
      toast.error(t('expenses.toasts.enterEmail'));
      return;
    }
    if (selectedColumns.length === 0) {
      toast.error(t('expenses.toasts.selectColumn'));
      return;
    }

    setIsSendingEmail(true);
    try {
      const employeeIdsToSend = selectedEmployeeIds.length === expenseCreators.length
        ? [] : selectedEmployeeIds;
      const projectIdsToSend = selectedProjectIds.length === projects.length
        ? [] : selectedProjectIds;
      const paymentMethodsToSend = (
        paymentMethodOptions.length > 0 &&
        selectedPaymentMethods.length === paymentMethodOptions.length + 1
      ) ? [] : selectedPaymentMethods;

      const response = await axios.post(`${API}/expenses/export/email`, {
        months: selectedMonths,
        employee_ids: employeeIdsToSend,
        project_ids: projectIdsToSend,
        payment_methods: paymentMethodsToSend,
        status_filter: selectedStatuses,
        columns: selectedColumns,
        to_email: emailAddress,
        include_receipts: includeReceipts
      });

      toast.success(response.data.message || 'Report sent successfully!');
      onOpenChange(false);
    } catch (error) {
      console.error('Email send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            {t('expenses.dialog.export')}
          </DialogTitle>
          <DialogDescription>
            {t('expenses.dialog.exportDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Month Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">{t('expenses.export.selectMonths')}</Label>
              <div className="flex items-center gap-2">
                <Label className="text-sm">{t('expenses.export.year')}</Label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <Button variant="ghost" size="sm" onClick={() => selectAllMonthsForYear(selectedYear)} data-testid="btn-select-all">
                  {t('expenses.export.selectAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => deselectAllMonthsForYear(selectedYear)} data-testid="btn-clear">
                  {t('common.clear')}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 bg-gray-50 rounded-lg">
              {MONTH_OPTIONS.map(m => (
                <div key={m.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`month-${selectedYear}-${m.value}`}
                    checked={isMonthSelected(selectedYear, m.value)}
                    onCheckedChange={() => toggleMonth(selectedYear, m.value)}
                  />
                  <label
                    htmlFor={`month-${selectedYear}-${m.value}`}
                    className="text-sm cursor-pointer"
                  >
                    {t(m.labelKey)}
                  </label>
                </div>
              ))}
            </div>
            {selectedMonths.length > 0 && (
              <div className="text-sm text-gray-600">
                {t('expenses.export.selected')} {selectedMonths
                  .sort((a, b) => a.year === b.year ? a.month - b.month : a.year - b.year)
                  .map(m => `${t(MONTH_OPTIONS[m.month - 1].labelKey)} ${m.year}`)
                  .join(', ')}
              </div>
            )}
          </div>

          {/* Employee Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">
                {t('expenses.export.selectEmployees')}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  {t('expenses.export.noneAllEmployees')}
                </span>
              </Label>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAllEmployees} data-testid="btn-select-all">
                  {t('expenses.export.selectAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAllEmployees} data-testid="btn-clear">
                  {t('common.clear')}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
              {expenseCreators.map(creator => (
                <div key={creator.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`emp-${creator.id}`}
                    checked={selectedEmployeeIds.includes(creator.id)}
                    onCheckedChange={() => toggleEmployee(creator.id)}
                  />
                  <label
                    htmlFor={`emp-${creator.id}`}
                    className="text-sm cursor-pointer truncate"
                    title={creator.name}
                  >
                    {creator.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Project Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">
                {t('expenses.export.filterProjects')}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  {t('expenses.export.noneAllProjects')}
                </span>
              </Label>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAllProjects} data-testid="export-select-all-projects">
                  {t('expenses.export.selectAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAllProjects} data-testid="export-clear-projects">
                  {t('common.clear')}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto" data-testid="export-projects-list">
              {projects.map(project => (
                <div key={project.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`proj-${project.id}`}
                    checked={selectedProjectIds.includes(project.id)}
                    onCheckedChange={() => toggleProject(project.id)}
                  />
                  <label
                    htmlFor={`proj-${project.id}`}
                    className="text-sm cursor-pointer truncate"
                    title={project.name}
                  >
                    {project.name}
                  </label>
                </div>
              ))}
              {projects.length === 0 && (
                <div className="text-sm text-gray-500 sm:col-span-2">{t('expenses.export.noProjects')}</div>
              )}
            </div>
          </div>

          {/* Payment Method Filter */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">
                {t('expenses.export.filterPaymentMethod')}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  {t('expenses.export.noneAll')}
                </span>
              </Label>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAllPaymentMethods} data-testid="export-select-all-pms">
                  {t('expenses.export.selectAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAllPaymentMethods} data-testid="export-clear-pms">
                  {t('common.clear')}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto" data-testid="export-pms-list">
              {paymentMethodOptions.map(pm => (
                <div key={pm} className="flex items-center space-x-2">
                  <Checkbox
                    id={`pm-${pm}`}
                    checked={selectedPaymentMethods.includes(pm)}
                    onCheckedChange={() => togglePaymentMethod(pm)}
                  />
                  <label htmlFor={`pm-${pm}`} className="text-sm cursor-pointer truncate" title={pm}>
                    {pm}
                  </label>
                </div>
              ))}
              <div key="__none__" className="flex items-center space-x-2">
                <Checkbox
                  id="pm-none"
                  checked={selectedPaymentMethods.includes('__none__')}
                  onCheckedChange={() => togglePaymentMethod('__none__')}
                />
                <label htmlFor="pm-none" className="text-sm cursor-pointer italic text-gray-600">
                  {t('expenses.export.noMethodSet')}
                </label>
              </div>
              {paymentMethodOptions.length === 0 && (
                <div className="text-sm text-gray-500 sm:col-span-2">
                  {t('expenses.export.noPaymentMethods')}
                </div>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">{t('expenses.export.statusFilter')}</Label>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelectedStatuses(STATUS_OPTIONS.map(s => s.key))} data-testid="btn-select-all">
                  {t('expenses.export.selectAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedStatuses([])} data-testid="btn-clear">
                  {t('common.clear')}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {STATUS_OPTIONS.map(status => (
                <div key={status.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${status.key}`}
                    checked={selectedStatuses.includes(status.key)}
                    onCheckedChange={() => toggleStatus(status.key)}
                  />
                  <label htmlFor={`status-${status.key}`} className="text-sm cursor-pointer">
                    {t(status.labelKey)}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Column Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">{t('expenses.export.columns')}</Label>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelectedColumns(AVAILABLE_COLUMNS.map(c => c.key))} data-testid="btn-select-all">
                  {t('expenses.export.selectAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedColumns([])} data-testid="btn-clear">
                  {t('common.clear')}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg">
              {AVAILABLE_COLUMNS.map(column => (
                <div key={column.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={`col-${column.key}`}
                    checked={selectedColumns.includes(column.key)}
                    onCheckedChange={() => toggleColumn(column.key)}
                  />
                  <label htmlFor={`col-${column.key}`} className="text-sm cursor-pointer">
                    {t(column.labelKey)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-4 sm:flex-col">
          {/* Email Section */}
          <div className="w-full space-y-3 pt-3 border-t">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-600" />
              <Label className="text-sm font-semibold">{t('expenses.export.sendEmail')}</Label>
            </div>
            <div className="flex items-center gap-3">
              <Input
                type="email"
                placeholder={t('expenses.ph.email')}
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="flex-1"
                data-testid="email-recipient-input"
              />
              <div className="flex items-center gap-2">
                <Switch
                  id="include-receipts"
                  checked={includeReceipts}
                  onCheckedChange={setIncludeReceipts}
                />
                <Label htmlFor="include-receipts" className="text-xs whitespace-nowrap">{t('expenses.export.attachReceipts')}</Label>
              </div>
            </div>
          </div>
          <div className="flex w-full justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSendEmail} disabled={isSendingEmail || !emailAddress} variant="secondary" data-testid="send-email-btn">
              {isSendingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              {isSendingEmail ? t('expenses.actions.sending') : t('expenses.actions.sendEmail')}
            </Button>
            <DocLangToggle testId="doc-lang-exportexpensesdialog-0" />
            <Button onClick={handleExport} disabled={isExporting} data-testid="export-excel-btn">
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? t('expenses.actions.exporting') : t('expenses.actions.exportExcel')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportExpensesDialog;
